import { Button, Divider, message } from "antd";
import NarinBreadcrumb from "components/Desktop/Breadcrumb";
import ProductImageGallery from "components/Desktop/ProductImageGallery";
import MobileProductImageGallery from "components/Mobile/ProductImageGallery";
import NarinLayout from "components/NarinLayout";
import NInputNumber from "components/Desktop/NInputNumber";
import Point from "components/Desktop/Point";
import WholesaleInfo from "components/Desktop/WholesaleInfo";
import CartIcon from "icons/CartIcon";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAddToCart } from "services/cart";
import { useGetProductById, useRequestProduct } from "services/product";
import { useGetSubcategory } from "services/category";
import { HeartOutlined, HeartFilled } from "@ant-design/icons";
import { useAddFavorite, useDeleteFavorite } from "services/favorite";
import { renderValueStockUnit } from "utils/text";
import useNotificationCenter from "hooks/useNotificationCenter";
import useUserStore from "stores/user";
import useLoginModal from "hooks/useLoginModal";
import RelatedProduct from "components/Desktop/RelatedProduct";
import { trackViewProduct } from "utils/analytics";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoading, productDetail } = useGetProductById(id || "");
  const [productQty, setProductQty] = useState<number>(1);
  // Fetch subcategory data when product has a parent category
  const parentCategoryId =
    productDetail?.category?.parentCategoryId?.toString();
  const { categoryList: subcategoryData, isLoading: isSubcategoryLoading } =
    useGetSubcategory(parentCategoryId || "");
  const { addFavoriteAsync } = useAddFavorite();
  const { deleteFavoriteAsync } = useDeleteFavorite();
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [favoriteState, setFavoriteState] = useState<boolean>(false);

  const { loginModal, showLogin } = useLoginModal();

  const { addToCartAsync } = useAddToCart();
  const { requestProductAsync, isRequesting } = useRequestProduct();
  const { user } = useUserStore();

  const { notificationCenter, showNotification, hideNotification } =
    useNotificationCenter();

  useEffect(() => {
    if (favoriteState) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300); // Duration of the animation
      return () => clearTimeout(timer);
    }
  }, [favoriteState]);

  useEffect(() => {
    if (productDetail) {
      if (productDetail._count.favorites) {
        setFavoriteState(true);
      } else {
        setFavoriteState(false);
      }
    }
  }, [productDetail]);

  // Reset product quantity to 1 when product id changes
  useEffect(() => {
    setProductQty(1);
  }, [id]);

  // Track product view when product is loaded
  useEffect(() => {
    if (productDetail) {
      trackViewProduct(
        productDetail.id,
        productDetail.name,
        productDetail.category?.name,
      );
    }
  }, [productDetail]);

  // Update page title when product is loaded
  useEffect(() => {
    if (productDetail) {
      document.title = `นรินทร์เภสัช ชัยภูมิ | ${productDetail.name}`;
    }
    return () => {
      document.title = "นรินทร์เภสัช ชัยภูมิ";
    };
  }, [productDetail]);

  // Add noindex meta tag for products in hidden categories
  useEffect(() => {
    const noIndexCategoryIds = [
      1, 2, 16, 11, 12, 175, 4, 5, 6, 3, 10, 7, 8, 13, 26, 24, 25, 18, 21, 19,
      20, 22, 176, 14, 15, 174, 27, 29, 28, 30, 31, 41, 273, 42, 44, 45, 43,
      173, 46, 47, 48, 49, 186,
    ];
    const categoryId = productDetail?.category?.id;
    const parentCategoryId = productDetail?.category?.parentCategoryId;
    const shouldNoIndex =
      categoryId &&
      (noIndexCategoryIds.includes(categoryId) ||
        (parentCategoryId && noIndexCategoryIds.includes(parentCategoryId)));

    if (shouldNoIndex) {
      let metaTag = document.querySelector('meta[name="robots"]');
      if (!metaTag) {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", "robots");
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute("content", "noindex, nofollow");
    }

    return () => {
      const metaTag = document.querySelector('meta[name="robots"]');
      if (metaTag) {
        metaTag.remove();
      }
    };
  }, [productDetail]);

  const price = useMemo(() => {
    if (!productDetail) return 0;
    const sortedPrices = productDetail.productPrices.sort(
      (a, b) => (b.minQuantity || 0) - (a.minQuantity || 0),
    );
    const applicablePrice =
      sortedPrices.find((price) => productQty >= (price.minQuantity || 0)) ||
      sortedPrices.at(-1);

    const finalPrice = applicablePrice?.price;
    return finalPrice ? Number(finalPrice) * productQty : 0;
  }, [productDetail, productQty]);

  const point = useMemo(() => {
    if (price) {
      return Math.floor(price / 25);
    }
    return 0;
  }, [price, productQty]);

  if (isLoading) {
    return <div />;
  }

  // Show Product Not Found screen if product doesn't exist
  if (!productDetail || !id) {
    return (
      <NarinLayout>
        <div className="bg-white p-8 md:p-16 mb-4 min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-6">🔍</div>
            <h1 className="text-3xl font-semibold mb-4 text-gray-800">
              ไม่พบสินค้า
            </h1>
            <p className="text-gray-600 mb-8">
              ขออภัย ไม่พบสินค้าที่คุณกำลังค้นหา
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Button
                size="large"
                onClick={() => navigate("/")}
                type="primary"
                className="px-8"
              >
                กลับหน้าหลัก
              </Button>
              <Button
                size="large"
                onClick={() => navigate(-1)}
                className="px-8"
              >
                กลับหน้าก่อนหน้า
              </Button>
            </div>
          </div>
        </div>
      </NarinLayout>
    );
  }

  // Show loading if subcategory data is still being fetched for products with parent categories
  if (parentCategoryId && isSubcategoryLoading) {
    return <div />;
  }

  const handleAddItemToCart = async () => {
    // Do nothing if product is out of stock
    if (productDetail.stockQuantity === 0) {
      return;
    }

    if (user) {
      // add item to cart
      if (productQty > 0) {
        showNotification();
        await addToCartAsync({
          productId: +id,
          quantity: productQty,
        });
        setTimeout(() => {
          hideNotification();
        }, 350);
      }
    } else {
      // open login modal
      showLogin();
    }
  };

  const handleOnChangeInput = (qty: number) => {
    setProductQty(qty);
  };

  const subCatagoryIdList = subcategoryData?.subCategories.map(
    (item) => item.id,
  );

  const renderBreadcrumb = () => {
    if (productDetail?.category?.parentCategoryId) {
      // Use subcategory data from API if available, fallback to productDetail data
      const parentCategoryName =
        subcategoryData?.name ||
        productDetail.category.parentCategory?.name ||
        "";

      return [
        {
          title: "สินค้าทั้งหมด",
          path: "",
        },
        {
          title: parentCategoryName,
          path: `/?categories=${
            productDetail.category.parentCategoryId
          },${subCatagoryIdList?.join(",")}`,
        },
        {
          title: productDetail.category.name,
          path: `/?categories=${productDetail.category.id}`,
        },
        {
          title: productDetail.name,
        },
      ];
    }
    if (
      productDetail?.category?.parentCategoryId === null &&
      productDetail.category.id
    ) {
      return [
        {
          title: "สินค้าทั้งหมด",
          path: "/",
        },

        {
          title: productDetail.category.name,
          path: `/?categories=${productDetail.category.id}`,
        },
        {
          title: productDetail.name,
        },
      ];
    }
    return [
      {
        title: "สินค้าทั้งหมด",
        path: "/",
      },
      {
        title: productDetail.name,
      },
    ];
  };
  return (
    <NarinLayout>
      <NarinBreadcrumb items={renderBreadcrumb()} />
      <div className="bg-white p-4 mb-4">
        <div className="hidden md:grid grid-cols-2 gap-6">
          <div>
            <ProductImageGallery
              images={productDetail.images || []}
              productName={productDetail.name}
              isOutOfStock={productDetail.stockQuantity === 0}
              productCode={productDetail.productCode}
            />
          </div>
          <div className="flex flex-auto flex-col gap-4">
            <div className="text-3xl font-medium">{productDetail.name}</div>
            <Point value={point} />
            <div>
              <span className="font-semibold">Generic name : </span>
              <span>{productDetail.genericName}</span>
            </div>
            <div>
              <span className="font-semibold">รหัสสินค้า : </span>
              <span>{productDetail.productCode}</span>
            </div>
            <div>
              <span className="font-semibold">บริษัท : </span>
              <span>{productDetail.manufacturer}</span>
            </div>
            <div>
              <span className="font-semibold">ขนาดบรรจุ : </span>
              <span>{renderValueStockUnit(productDetail.qtyPerUnit)}</span>
            </div>
            <WholesaleInfo
              priceList={productDetail.productPrices}
              unit={productDetail.unit}
            />
            <Divider className="m-0" />
            <div className="flex  justify-between pr-16">
              <div className="flex gap-4 items-center">
                <div>จำนวน</div>
                <NInputNumber
                  value={productQty}
                  onChange={handleOnChangeInput}
                />
              </div>
              <div className="text-primary-700 text-2xl font-semibold">
                {price?.toLocaleString()} บาท
              </div>
            </div>
            <div className="gap-4 text-start lg:text-end mt-8">
              <Button
                className={`px-8 ${isAnimating ? "animate" : ""}`}
                size="large"
                onClick={() => {
                  if (favoriteState) {
                    deleteFavoriteAsync(productDetail.id);
                    setFavoriteState(false);
                  } else {
                    setFavoriteState(true);
                    addFavoriteAsync(productDetail.id);
                  }
                }}
              >
                {favoriteState ? (
                  <HeartFilled style={{ fontSize: "24px" }} />
                ) : (
                  <HeartOutlined style={{ fontSize: "24px" }} />
                )}{" "}
                <span>ติดตามสินค้า</span>
              </Button>
              {productDetail.stockQuantity > 0 ? (
                <Button
                  onClick={handleAddItemToCart}
                  type="primary"
                  className="text-white px-8 ml-4"
                  size="large"
                >
                  <CartIcon />
                  <span>เพิ่มลงตะกร้าสินค้า</span>
                </Button>
              ) : (
                <Button
                  type="default"
                  size="large"
                  loading={isRequesting}
                  onClick={async () => {
                    if (!user) {
                      // open login modal if user is not logged in
                      showLogin();
                      return;
                    }
                    try {
                      await requestProductAsync(productDetail.id.toString());
                      message.success(
                        "ขอบคุณที่แจ้งความต้องการสินค้า เราจะแจ้งให้ทราบเมื่อมีสินค้า",
                      );
                    } catch (error) {
                      message.error("เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง");
                      console.error("Error requesting product:", error);
                    }
                  }}
                  className="bg-white text-secondary-600 border-secondary-600 px-8 ml-4"
                >
                  <span>ต้องการสินค้านี้</span>
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="block md:hidden relative">
          <div className="text-2xl font-medium">{productDetail.name}</div>
          <div className="block mt-4 text-center">
            <div className="relative">
              <div
                className={`absolute right-2 top-2 cursor-pointer text-primary z-10 ${
                  isAnimating ? "animate" : ""
                }`}
                onClick={() => {
                  if (favoriteState) {
                    deleteFavoriteAsync(productDetail.id);
                    setFavoriteState(false);
                  } else {
                    setFavoriteState(true);
                    addFavoriteAsync(productDetail.id);
                  }
                }}
              >
                {favoriteState ? (
                  <HeartFilled style={{ fontSize: "24px" }} />
                ) : (
                  <HeartOutlined style={{ fontSize: "24px" }} />
                )}
              </div>
              <MobileProductImageGallery
                images={productDetail.images || []}
                productName={productDetail.name}
                isOutOfStock={productDetail.stockQuantity === 0}
                productCode={productDetail.productCode}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="self-end">
              <Point value={point} />
            </div>
            <div>
              <span className="font-semibold">Generic name : </span>
              <span>{productDetail.genericName}</span>
            </div>
            <div>
              <span className="font-semibold">รหัสสินค้า : </span>
              <span>{productDetail.productCode}</span>
            </div>
            <div>
              <span className="font-semibold">บริษัท : </span>
              <span>{productDetail.manufacturer}</span>
            </div>
            <div>
              <span className="font-semibold">ขนาดบรรจุ : </span>
              <span>{renderValueStockUnit(productDetail.qtyPerUnit)}</span>
            </div>
            <WholesaleInfo
              priceList={productDetail.productPrices}
              unit={productDetail.unit}
            />
            <Divider className="m-0" />
            <div className="flex justify-between">
              <div className="font-semibold">จำนวน</div>
              <NInputNumber value={productQty} onChange={handleOnChangeInput} />
            </div>
            <div className="flex justify-between">
              <div className="font-semibold">รวมราคา</div>
              <div className="text-primary-700 text-2xl font-semibold">
                ฿{price?.toLocaleString()}
              </div>
            </div>
            <div className="mt-4 text-center">
              {productDetail.stockQuantity > 0 ? (
                <Button
                  onClick={handleAddItemToCart}
                  type="primary"
                  className="text-white px-8 ml-4"
                  size="large"
                >
                  <CartIcon />
                  <span>เพิ่มลงตะกร้าสินค้า</span>
                </Button>
              ) : (
                <Button
                  type="default"
                  size="large"
                  loading={isRequesting}
                  onClick={async () => {
                    if (!user) {
                      // open login modal if user is not logged in
                      showLogin();
                      return;
                    }
                    try {
                      await requestProductAsync(productDetail.id.toString());
                      message.success(
                        "ขอบคุณที่แจ้งความต้องการสินค้า เราจะแจ้งให้ทราบเมื่อมีสินค้า",
                      );
                    } catch (error) {
                      message.error("เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง");
                      console.error("Error requesting product:", error);
                    }
                  }}
                  className="bg-white text-secondary-600 border-secondary-600 px-8 ml-4"
                >
                  <span>ต้องการสินค้านี้</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white p-4 py-8 mb-4">
        <RelatedProduct mainProductId={+id} />
      </div>
      {notificationCenter}
      {loginModal}
    </NarinLayout>
  );
};
export default ProductPage;
